import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle,
  MessageSquare,
  Plus,
  RefreshCw,
  Send,
  User,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { communityService } from '../../services/communityService';
import type { CommunityComment } from '../../types/community';
import {
  formatCommentDate,
  getCommentStatusLabel,
  getCommentStatusStyle,
  getReplyDisplayName,
} from '../../utils/communityLabels';
import PortalPageHeader from '../../components/student-portal/PortalPageHeader';
import { getGradeInArabic } from '../../utils/gradeTranslations';

const PAGE_SIZE = 10;

type Tab = 'feed' | 'mine';

function CommentCard({
  comment,
  showStatus,
}: {
  comment: CommunityComment;
  showStatus?: boolean;
}) {
  return (
    <article className="space-y-3 rounded-2xl border-2 border-sage-200 bg-[#F7FAFC] p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary-dark">
            <User size={16} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-primary-dark">
              {comment.studentName || 'طالب'}
            </p>
            <p className="text-xs text-slate-500">{formatCommentDate(comment.createdAt)}</p>
          </div>
        </div>
        {showStatus && (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${getCommentStatusStyle(comment.status)}`}
          >
            {getCommentStatusLabel(comment.status)}
          </span>
        )}
      </div>

      <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{comment.text}</p>

      {comment.replies.length > 0 && (
        <div className="space-y-2 border-t border-sage-200 pt-3">
          <p className="text-xs font-semibold text-slate-500">الردود ({comment.replies.length})</p>
          {comment.replies.map((reply) => (
            <div
              key={reply.id}
              className="space-y-1 rounded-xl border border-sage-100 bg-primary-light/50 p-3 text-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-primary-dark">{getReplyDisplayName(reply)}</span>
                <span className="shrink-0 text-xs text-slate-500">
                  {formatCommentDate(reply.createdAt)}
                </span>
              </div>
              <p className="whitespace-pre-wrap text-slate-600">{reply.text}</p>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

const PortalForum: React.FC = () => {
  const { currentUser } = useAuth();
  const grade = currentUser?.grade || 'Secondary1';
  const [tab, setTab] = useState<Tab>('feed');
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCompose, setShowCompose] = useState(false);
  const [newText, setNewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const fetchComments = useCallback(
    async (targetTab: Tab, targetPage: number) => {
      setLoading(true);
      setError('');

      if (targetTab === 'feed') {
        const result = await communityService.getGradeComments(grade, targetPage, PAGE_SIZE, 'Approved');
        if (result.success) {
          setComments(result.data.comments);
          setTotalPages(Math.max(result.data.totalPages, 1));
          setPage(result.data.page);
        } else {
          setComments([]);
          setError(result.error);
        }
      } else {
        const result = await communityService.getMyPendingComments();
        if (result.success) {
          setComments(result.data);
          setTotalPages(1);
          setPage(1);
        } else {
          setComments([]);
          setError(result.error);
        }
      }

      setLoading(false);
    },
    [grade]
  );

  useEffect(() => {
    fetchComments(tab, 1);
  }, [tab, fetchComments]);

  useEffect(() => {
    if (!showCompose && !showSuccessPopup) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCompose(false);
        setShowSuccessPopup(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showCompose, showSuccessPopup]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text) {
      setSubmitError('اكتب سؤالك أولاً');
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    const result = await communityService.createGradeComment(grade, text);
    setSubmitting(false);

    if (result.success) {
      setNewText('');
      setShowCompose(false);
      setShowSuccessPopup(true);
      setTab('mine');
      fetchComments('mine', 1);
    } else {
      setSubmitError(result.error);
    }
  };

  return (
    <div className="relative px-4 pb-8 pt-4 sm:px-6 sm:pt-6 lg:px-8" dir="rtl">
      <div className="mb-5 rounded-2xl border-2 border-sage-200 bg-[#F7FAFC] px-4 py-5 sm:mb-7 sm:px-6 sm:py-6">
        <PortalPageHeader
          eyebrow="المنتدى"
          title={`منتدى ${getGradeInArabic(grade)}`}
          description="اسأل وشارك مع زملائك، والردود تظهر بعد المراجعة"
          icon={<MessageSquare className="h-4 w-4" />}
        />
      </div>

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setTab('feed')}
          className={`inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-3 text-sm font-bold sm:flex-none sm:px-5 ${
            tab === 'feed'
              ? 'bg-sage-600 text-white shadow-md shadow-sage-700/20'
              : 'border-2 border-sage-200 bg-[#F7FAFC] text-primary-dark hover:bg-primary-light'
          }`}
        >
          تعليقات المجتمع
        </button>
        <button
          type="button"
          onClick={() => setTab('mine')}
          className={`inline-flex min-h-11 flex-1 items-center justify-center rounded-xl px-3 text-sm font-bold sm:flex-none sm:px-5 ${
            tab === 'mine'
              ? 'bg-sage-600 text-white shadow-md shadow-sage-700/20'
              : 'border-2 border-sage-200 bg-[#F7FAFC] text-primary-dark hover:bg-primary-light'
          }`}
        >
          أسئلتي
        </button>
        <button
          type="button"
          onClick={() => fetchComments(tab, page)}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border-2 border-sage-200 bg-[#F7FAFC] text-primary-dark hover:bg-primary-light"
          aria-label="تحديث"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-sage-200 border-t-sage-600" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-700">
          {error}
        </div>
      ) : comments.length === 0 ? (
        <div className="rounded-xl border-2 border-sage-200 bg-[#F7FAFC] py-12 text-center">
          <MessageSquare className="mx-auto mb-3 h-12 w-12 text-sage-300" />
          <p className="text-slate-500">
            {tab === 'feed' ? 'لا توجد تعليقات منشورة بعد' : 'لم ترسل أي أسئلة بعد'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {comments.map((comment) => (
            <CommentCard key={comment.id} comment={comment} showStatus={tab === 'mine'} />
          ))}
        </div>
      )}

      {tab === 'feed' && totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            type="button"
            onClick={() => fetchComments('feed', Math.max(page - 1, 1))}
            disabled={page <= 1 || loading}
            className="rounded-xl border-2 border-sage-200 bg-white px-4 py-2 text-sm font-semibold text-primary-dark disabled:opacity-40"
          >
            السابق
          </button>
          <span className="rounded-xl bg-sage-600 px-4 py-2 text-sm font-semibold text-white">
            {page} من {totalPages}
          </span>
          <button
            type="button"
            onClick={() => fetchComments('feed', Math.min(page + 1, totalPages))}
            disabled={page >= totalPages || loading}
            className="rounded-xl border-2 border-sage-200 bg-white px-4 py-2 text-sm font-semibold text-primary-dark disabled:opacity-40"
          >
            التالي
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowCompose(true)}
        className="fixed bottom-24 left-6 z-40 inline-flex min-h-12 items-center gap-2 rounded-full bg-sage-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-sage-700/30 hover:bg-sage-700"
      >
        <Plus className="h-4 w-4" />
        سؤال جديد
      </button>

      {showCompose && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={() => setShowCompose(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-2xl border-2 border-sage-200 bg-[#F7FAFC] p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-extrabold text-primary-dark">سؤال جديد</h3>
              <button
                type="button"
                onClick={() => setShowCompose(false)}
                className="rounded-xl p-2 text-slate-400 hover:bg-primary-light"
                aria-label="إغلاق"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <textarea
                value={newText}
                onChange={(e) => {
                  setNewText(e.target.value);
                  setSubmitError('');
                }}
                rows={5}
                placeholder="اكتب سؤالك هنا..."
                className="w-full rounded-xl border-2 border-sage-200 bg-white px-3 py-3 text-sm focus:border-sage-500 focus:outline-none"
                disabled={submitting}
              />
              {submitError && <p className="text-sm text-red-600">{submitError}</p>}
              <button
                type="submit"
                disabled={submitting || !newText.trim()}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-sage-600 px-4 text-sm font-semibold text-white hover:bg-sage-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'جاري الإرسال...' : 'إرسال السؤال'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showSuccessPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowSuccessPopup(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-2xl border-2 border-sage-200 bg-[#F7FAFC] p-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-light text-primary-dark">
              <CheckCircle className="h-8 w-8" />
            </div>
            <h3 className="font-display mb-2 text-xl font-extrabold text-primary-dark">تم إرسال سؤالك</h3>
            <p className="mb-6 text-sm text-slate-600">هيظهر بعد المراجعة من الإدارة</p>
            <button
              type="button"
              onClick={() => setShowSuccessPopup(false)}
              className="min-h-11 w-full rounded-xl bg-sage-600 text-sm font-semibold text-white hover:bg-sage-700"
            >
              تمام
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalForum;
